import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, message, Table, TableProps, Tabs, TabsProps } from "antd";
import { useTabParam } from "hooks/useTabParam";
import { ClassTypeBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { th, tnum } from "shared/components/TableText";
import { PAGE_SIZE } from "shared/constants";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { localTzDate, now, tzDate } from "shared/utils/dayjs";
import { InstructorSessionRow } from "shared/types";
import { ClassCategory } from "shared/types/enum";

// Same columns for both sub-tabs below, only the sort direction differs:
// Mendatang reads soonest-first (the next class is the most relevant one),
// Selesai reads most-recent-first (matching every other analytics table's
// convention). No status column here — split into separate tabs instead of
// one flat list with a status badge, so which bucket a row is in already
// says that.
const buildColumns = (
  sortAscending: boolean,
): TableProps<InstructorSessionRow>["columns"] => [
  {
    title: th(translate("analyticsPage.sales.col_class")),
    dataIndex: "class_name",
    render: (name: string) => <span style={{ fontWeight: 600 }}>{name}</span>,
  },
  {
    title: th(translate("analyticsPage.class.col_type")),
    dataIndex: "class_type",
    render: (type: ClassCategory) => <ClassTypeBadge type={type} />,
    filters: [
      { value: ClassCategory.Open, text: "Open" },
      { value: ClassCategory.Private, text: "Private" },
    ],
    onFilter: (value, record) =>
      record.class_type.toLowerCase().includes((value as string).toLowerCase()),
  },
  {
    title: th(translate("analyticsPage.sales.col_start_time")),
    dataIndex: "start_time",
    render: (value: string) => localTzDate(value).format("DD MMM YYYY, HH:mm"),
    sorter: (a, b) => a.start_time.localeCompare(b.start_time),
    defaultSortOrder: sortAscending ? ("ascend" as const) : ("descend" as const),
  },
  {
    title: th(translate("analyticsPage.sales.col_attendees")),
    dataIndex: "attendees",
    render: (value: number) => <span style={tnum}>{value}</span>,
    sorter: (a, b) => a.attendees - b.attendees,
  },
];

const SessionTable = ({
  sessions,
  isLoading,
  sortAscending,
  onRowClick,
}: {
  sessions: InstructorSessionRow[];
  isLoading: boolean;
  sortAscending: boolean;
  onRowClick: (session: InstructorSessionRow) => void;
}) => (
  <Table<InstructorSessionRow>
    sticky
    rowKey="session_key"
    showSorterTooltip={false}
    size="middle"
    loading={isLoading}
    columns={buildColumns(sortAscending)}
    dataSource={sessions}
    pagination={{ pageSize: PAGE_SIZE, showSizeChanger: false }}
    onRow={(row) => ({
      onClick: () => onRowClick(row),
      style: { cursor: row.is_schedule_deleted ? "not-allowed" : "pointer" },
    })}
  />
);

// One row per class this instructor taught (or has coming up), not one
// customer's booking — split into two sub-tabs (own ?sessionStatus= URL
// param, a sibling of the outer ?tab= like CustomerBooking's own
// ?bookingStatus= param). Clicking a row goes straight to that occurrence's
// Detail Jadwal page (same target + deleted-schedule guard
// CustomerBookingTable's own row click uses), not a summary modal — an
// admin looking at an instructor's history wants the real schedule, not
// another layer to click through.
const InstructorSessionHistory = ({
  sessions,
  isLoading,
}: {
  sessions: InstructorSessionRow[];
  isLoading: boolean;
}) => {
  const navigate = useNavigate();
  const { activeKey, onChange } = useTabParam("upcoming", "sessionStatus", [
    "upcoming",
    "finished",
  ]);

  const grouped = useMemo(() => {
    const current = now();
    return {
      upcoming: sessions.filter((s) => current.isBefore(tzDate(s.start_time))),
      finished: sessions.filter((s) => !current.isBefore(tzDate(s.start_time))),
    };
  }, [sessions]);

  const handleRowClick = (session: InstructorSessionRow) => {
    if (session.is_schedule_deleted) {
      message.info(translate("messages.schedule_deleted"));
      return;
    }
    navigate(
      `${PATHS.SCHEDULE}/${session.schedule_id}?occurrence_timestamp=${encodeURIComponent(session.start_time)}`,
    );
  };

  const items: TabsProps["items"] = [
    {
      key: "upcoming",
      label: `${translate("label.class_history_upcoming")} (${grouped.upcoming.length})`,
      children: (
        <SessionTable
          sessions={grouped.upcoming}
          isLoading={isLoading}
          sortAscending
          onRowClick={handleRowClick}
        />
      ),
    },
    {
      key: "finished",
      label: `${translate("label.class_history_finished")} (${grouped.finished.length})`,
      children: (
        <SessionTable
          sessions={grouped.finished}
          isLoading={isLoading}
          sortAscending={false}
          onRowClick={handleRowClick}
        />
      ),
    },
  ];

  return (
    <Card styles={{ body: { padding: 18 } }}>
      <Tabs size="small" activeKey={activeKey} onChange={onChange} items={items} />
    </Card>
  );
};

export default InstructorSessionHistory;
